/**
 * Admin API — trial invites without proforma (variant 5b).
 *
 * @see docs/specifications/2026-09-30-trial-invite-bez-proformy/spec.md
 */
import apiClient from "./apiClient";
import { extractErrorMessage } from "../utils/extractErrorMessage";

export type TrialInviteStatus = "active" | "used" | "expired";

export interface TrialInvite {
  id: string;
  email: string | null;
  trialDays: number;
  expiresAt: string;
  usedAt: string | null;
  usedByTenantId: string | null;
  createdAt: string;
  status: TrialInviteStatus;
  /** Used, but the tenant has no billing subscription (trial order failed). null = unknown. */
  subscriptionMissing?: boolean | null;
}

export interface CreateTrialInvitePayload {
  brandId: string;
  email?: string;
  trialDays?: number;
  validDays?: number;
}

export interface CreatedTrialInvite {
  invite: TrialInvite;
  /** Registration link with the raw token — shown once, never retrievable again. */
  url: string;
}

export async function listTrialInvites(
  brandId: string,
): Promise<TrialInvite[]> {
  const response = await apiClient.get<{ data: TrialInvite[] }>(
    `/admin/trial-invites?brandId=${encodeURIComponent(brandId)}`,
  );
  return response.data.data;
}

export async function createTrialInvite(
  payload: CreateTrialInvitePayload,
): Promise<CreatedTrialInvite> {
  const response = await apiClient.post<CreatedTrialInvite>(
    "/admin/trial-invites",
    payload,
  );
  return response.data;
}

/** Why a revoke failed — only "failed" is worth retrying. */
export type RevokeTrialInviteFailure = "used" | "not-found" | "failed";

/** Revoke failure with a Polish, user-facing message. */
export class RevokeTrialInviteError extends Error {
  readonly reason: RevokeTrialInviteFailure;

  constructor(reason: RevokeTrialInviteFailure, message: string) {
    super(message);
    this.name = "RevokeTrialInviteError";
    this.reason = reason;
  }
}

const toRevokeError = (error: unknown): RevokeTrialInviteError => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 409) {
    return new RevokeTrialInviteError(
      "used",
      extractErrorMessage(
        error,
        "Zaproszenie zostało już użyte — nie można go odwołać.",
      ),
    );
  }
  if (status === 404) {
    return new RevokeTrialInviteError(
      "not-found",
      "Zaproszenie nie istnieje — odśwież listę.",
    );
  }
  return new RevokeTrialInviteError(
    "failed",
    "Nie udało się odwołać zaproszenia.",
  );
};

/**
 * Kills an unused link at once (it shows as expired afterwards). Already
 * expired → success (idempotent); used → 409. The response body is ignored —
 * callers refetch the list.
 */
export async function revokeTrialInvite(id: string): Promise<void> {
  try {
    await apiClient.post(
      `/admin/trial-invites/${encodeURIComponent(id)}/revoke`,
    );
  } catch (error) {
    throw toRevokeError(error);
  }
}
